import type { AccentHue } from "@repo/fika-design-system/color"
import type { IconName } from "@repo/fika-design-system/icon"
import type { JSX } from "solid-js"
import { createSignal, onCleanup, Show } from "solid-js"
import { useI18nContext } from "@/i18n/i18n-solid"
import {
  holdButtonContent,
  holdButtonLabelCompleted,
  holdButtonLabelDefault,
  holdButtonLabelProgress,
  holdButtonLabels,
  holdButtonRoot,
  holdFillBase,
  holdFillRecipe,
} from "./hold-button.css"
import { Icon } from "./icon"

interface Props {
  onHoldComplete: () => void
  hue?: AccentHue
  duration?: number
  class?: string
  disabled?: boolean
  icon?: IconName
  label?: string
  labelProgress?: string
  labelCompleted?: string
}

const COMPLETED_DURATION_MS = 3000

export function HoldButton(props: Props) {
  const { LL } = useI18nContext()
  const duration = () => props.duration ?? 1000
  const hue = () => props.hue ?? "rose"
  const [isHolding, setIsHolding] = createSignal(false)
  const [isCompleted, setIsCompleted] = createSignal(false)
  let timeoutId: ReturnType<typeof setTimeout> | undefined
  let completedTimeoutId: ReturnType<typeof setTimeout> | undefined

  onCleanup(() => {
    if (timeoutId !== undefined) clearTimeout(timeoutId)
    if (completedTimeoutId !== undefined) clearTimeout(completedTimeoutId)
  })

  function handlePointerDown(event: PointerEvent) {
    if (event.button !== 0) return
    if (props.disabled) return
    setIsHolding(true)
    timeoutId = setTimeout(() => {
      props.onHoldComplete()
      timeoutId = undefined
      setIsHolding(false)
      setIsCompleted(true)
      completedTimeoutId = setTimeout(() => {
        setIsCompleted(false)
        completedTimeoutId = undefined
      }, COMPLETED_DURATION_MS)
    }, duration())
  }

  function handlePointerUp() {
    setIsHolding(false)
    if (timeoutId !== undefined) {
      clearTimeout(timeoutId)
      timeoutId = undefined
    }
  }

  const restLabel = () => props.label ?? LL().common.holdToConfirm()

  return (
    <button
      type="button"
      disabled={props.disabled}
      data-hold-button
      data-holding={isHolding() ? "" : undefined}
      data-completed={isCompleted() ? "" : undefined}
      class={[props.class, holdButtonRoot({ hue: hue() })]
        .filter(Boolean)
        .join(" ")}
      style={{ "--hold-duration": `${duration()}ms` } as JSX.CSSProperties}
      aria-label={restLabel()}
      onPointerDown={handlePointerDown}
      onPointerUp={handlePointerUp}
      onPointerLeave={handlePointerUp}
      onPointerCancel={handlePointerUp}
    >
      <div
        aria-hidden="true"
        class={[holdFillBase, holdFillRecipe({ hue: hue() })].join(" ")}
      />
      <span class={holdButtonContent}>
        <Show when={props.icon}>
          {iconName => <Icon name={iconName()} size="18" />}
        </Show>
        <span class={holdButtonLabels}>
          <span class={holdButtonLabelDefault}>
            {props.label ?? LL().common.holdToConfirm()}
          </span>
          <span class={holdButtonLabelProgress}>
            {props.labelProgress ?? "..."}
          </span>
          <span class={holdButtonLabelCompleted}>
            {props.labelCompleted ?? LL().common.done()}
          </span>
        </span>
      </span>
    </button>
  )
}
